/**
 * Sign-in / Sign-up screen — email + password. Toggles between modes.
 * Rendered by AuthGate when no Firebase user is signed in. On success the
 * auth-state listener in AuthGate flips and the children render.
 */

import { useState } from "react";
import { signIn, signUp, getConfigError } from "../lib/firebaseAuth";
import { TERMINAL_THEMES } from "../types";

interface Props {
  /** Optional theme override. Falls back to the default executive theme. */
  themeId?: string;
}

type Mode = "signin" | "signup";

export function SignInScreen({ themeId }: Props) {
  const theme =
    TERMINAL_THEMES.find((t) => t.id === themeId) || TERMINAL_THEMES[0];
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const configErr = getConfigError();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      if (mode === "signup") {
        await signUp(email, password);
      } else {
        await signIn(email, password);
      }
      // No state update here — onAuthStateChanged fires and AuthGate
      // swaps to the dashboard.
    } catch (err: any) {
      const code = err?.code?.replace(/^auth\//, "") || "unknown";
      const msg = err?.message || `${mode} failed`;
      setError(`${code}: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  const heading = mode === "signup" ? "Create your account" : "Sign in";
  const cta = mode === "signup" ? "Sign up" : "Sign in";
  const busyLabel = mode === "signup" ? "Creating…" : "Signing in…";
  const toggleHint =
    mode === "signup"
      ? { prefix: "Already have an account?", action: "Sign in" }
      : { prefix: "New here?", action: "Create an account" };

  return (
    <div
      style={{ backgroundColor: theme.bg, color: theme.fg }}
      className="min-h-screen w-full flex items-center justify-center px-4"
    >
      <form
        onSubmit={onSubmit}
        style={{
          backgroundColor: theme.inputBg,
          borderColor: theme.border,
        }}
        className="w-full max-w-sm border rounded p-6 space-y-4"
      >
        <div>
          <h1
            style={{ color: theme.fg }}
            className="text-base font-bold tracking-tight"
          >
            {heading}
          </h1>
          <p
            style={{ color: theme.systemText }}
            className="text-[11px] mt-1"
          >
            Agentic Operator Command Center
          </p>
        </div>

        {configErr && (
          <div
            style={{
              color: "#b45309",
              backgroundColor: "#f59e0b15",
              borderColor: "#f59e0b55",
            }}
            className="border rounded text-[11px] px-2.5 py-2"
          >
            {configErr}
          </div>
        )}

        <label className="block">
          <span
            style={{ color: theme.systemText }}
            className="text-[10px] font-bold uppercase tracking-widest"
          >
            Email
          </span>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{
              backgroundColor: theme.bg,
              borderColor: theme.border,
              color: theme.fg,
            }}
            className="mt-1 w-full border rounded px-2 py-1.5 text-sm focus:outline-none"
          />
        </label>

        <label className="block">
          <span
            style={{ color: theme.systemText }}
            className="text-[10px] font-bold uppercase tracking-widest"
          >
            Password
          </span>
          <input
            type="password"
            autoComplete={mode === "signup" ? "new-password" : "current-password"}
            required
            minLength={mode === "signup" ? 8 : undefined}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{
              backgroundColor: theme.bg,
              borderColor: theme.border,
              color: theme.fg,
            }}
            className="mt-1 w-full border rounded px-2 py-1.5 text-sm focus:outline-none"
          />
          {mode === "signup" && (
            <span
              style={{ color: theme.systemText }}
              className="block text-[10px] mt-1"
            >
              Minimum 8 characters.
            </span>
          )}
        </label>

        {error && (
          <div
            style={{
              color: "#dc2626",
              backgroundColor: "#dc262615",
              borderColor: "#dc262655",
            }}
            className="border rounded text-[11px] px-2.5 py-2"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={busy || !!configErr}
          style={{
            backgroundColor: theme.accent,
            color: theme.bg,
            opacity: busy || configErr ? 0.6 : 1,
          }}
          className="w-full py-2 rounded text-sm font-semibold cursor-pointer"
        >
          {busy ? busyLabel : cta}
        </button>

        <div className="text-center text-[11px]" style={{ color: theme.systemText }}>
          {toggleHint.prefix}{" "}
          <button
            type="button"
            onClick={() => {
              setMode(mode === "signup" ? "signin" : "signup");
              setError(null);
            }}
            style={{ color: theme.accent }}
            className="font-semibold underline cursor-pointer"
          >
            {toggleHint.action}
          </button>
        </div>
      </form>
    </div>
  );
}
